/**
 * @zendir/ui - Divider
 *
 * Visual separator line following Astro UX Design System.
 * Theme-aware with muted border colors. Supports horizontal and vertical orientation.
 *
 * @example
 * ```tsx
 * <VStack spacing="md">
 *   <Section1 />
 *   <Divider />
 *   <Section2 />
 * </VStack>
 *
 * <HStack spacing="md">
 *   <Panel1 />
 *   <Divider orientation="vertical" />
 *   <Panel2 />
 * </HStack>
 *
 * <Divider label="OR" />
 * ```
 */

import React, { memo } from "react";
import { useTheme } from "../../theme";

export interface DividerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Divider orientation */
  orientation?: "horizontal" | "vertical";
  /** Optional centered label text */
  label?: string;
  /** Spacing around the divider (CSS value) */
  spacing?: string;
  /** Color override */
  color?: string;
  /** Dashed style */
  dashed?: boolean;
}

export const Divider = memo(function Divider({
  orientation = "horizontal",
  label,
  spacing,
  color,
  dashed = false,
  style,
  ...rest
}: DividerProps): React.ReactElement {
  const { tokens } = useTheme();

  const lineColor = color || tokens.colors.border.muted;
  const borderStyle = dashed ? "dashed" : "solid";

  if (orientation === "vertical") {
    return (
      <div
        role="separator"
        aria-orientation="vertical"
        style={{
          width: "1px",
          alignSelf: "stretch",
          backgroundColor: lineColor,
          flexShrink: 0,
          ...(spacing ? { margin: `0 ${spacing}` } : {}),
          ...(dashed
            ? {
                backgroundColor: "transparent",
                borderLeft: `1px ${borderStyle} ${lineColor}`,
              }
            : {}),
          ...style,
        }}
        {...rest}
      />
    );
  }

  // Horizontal with optional label
  if (label) {
    return (
      <div
        role="separator"
        style={{
          display: "flex",
          alignItems: "center",
          gap: tokens.spacing.sm,
          ...(spacing ? { margin: `${spacing} 0` } : {}),
          ...style,
        }}
        {...rest}
      >
        <div
          style={{
            flex: 1,
            height: "1px",
            backgroundColor: lineColor,
            ...(dashed
              ? {
                  backgroundColor: "transparent",
                  borderTop: `1px ${borderStyle} ${lineColor}`,
                }
              : {}),
          }}
        />
        <span
          style={{
            fontSize: tokens.typography.fontSize.xs,
            fontWeight: tokens.typography.fontWeight.medium,
            color: tokens.colors.text.tertiary,
            fontFamily: tokens.typography.fontFamily.primary,
            letterSpacing: tokens.typography.letterSpacing.wide,
            textTransform: "uppercase",
            flexShrink: 0,
            padding: `0 ${tokens.spacing.xs}`,
          }}
        >
          {label}
        </span>
        <div
          style={{
            flex: 1,
            height: "1px",
            backgroundColor: lineColor,
            ...(dashed
              ? {
                  backgroundColor: "transparent",
                  borderTop: `1px ${borderStyle} ${lineColor}`,
                }
              : {}),
          }}
        />
      </div>
    );
  }

  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      style={{
        width: "100%",
        height: "1px",
        backgroundColor: lineColor,
        flexShrink: 0,
        ...(spacing ? { margin: `${spacing} 0` } : {}),
        ...(dashed
          ? {
              backgroundColor: "transparent",
              borderTop: `1px ${borderStyle} ${lineColor}`,
            }
          : {}),
        ...style,
      }}
      {...rest}
    />
  );
});

export default Divider;
